import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { bookingService } from '@/api/services/bookingService'
import { useUpdateSessionStatusOnBehalf } from '@/api/hooks'
import { toast } from '@/store/useToastStore'
import { Modal } from '@/shared/ui/Modal'
import { Textarea } from '@/shared/ui/Input'
import { cn, DUMMY_AVATAR, onAvatarError, pickTranslation, localized } from '@/shared/utils'
import {
  Calendar, MapPin, Phone, Mail, ChevronLeft, ChevronRight,
  CalendarClock, Car, Package, CreditCard, Infinity as InfinityIcon, Clock,
  Check, X,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDateTime(v?: string | null) {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  } catch { return v }
}
function translated(list: any[] | undefined, lang: string, fallback = '—') {
  return pickTranslation(list, lang)?.name ?? fallback
}

// ── Status badge ──────────────────────────────────────────────────────────────
// Returns the badge colour + an i18n key (falls back to the raw status for unknowns).
function sessionStatusCfg(s?: string) {
  const u = s?.toUpperCase()
  if (u === 'PENDING')     return { cls: 'bg-brand-500 text-white',  key: 'status.pending'    }
  if (u === 'CONFIRMED')   return { cls: 'bg-brand-500 text-white',  key: 'status.confirmed'  }
  if (u === 'COMPLETED')   return { cls: 'bg-green-500 text-white',  key: 'status.completed'  }
  if (u === 'CANCELLED')   return { cls: 'bg-red-500 text-white',    key: 'status.cancelled'  }
  if (u === 'IN_PROGRESS' || u === 'IN PROGRESS')
                           return { cls: 'bg-purple-500 text-white', key: 'status.inProgress' }
  return { cls: 'bg-gray-200 text-gray-600', key: s ?? '—' }
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
function StatTile({ label, value, icon, bg, iconBg, textColor }: {
  label: string; value: number | string
  icon: React.ReactNode; bg: string; iconBg: string; textColor: string
}) {
  return (
    <div className={cn('rounded-2xl p-4 flex items-center gap-3 shadow-sm border border-white/60', bg)}>
      <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0', iconBg)}>{icon}</div>
      <div>
        <div className="text-[12px] font-bold uppercase tracking-wider text-gray-400">{label}</div>
        <div className={cn('text-2xl font-black mt-0.5', textColor)}>{value}</div>
      </div>
    </div>
  )
}

// ── Session request card ──────────────────────────────────────────────────────
function SessionRequestCard({ row }: { row: any }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const booking  = row.booking ?? {}
  const user     = booking.user ?? {}
  const driver   = row.driver_details ?? booking.driver ?? {}
  const userName   = localized(user, 'name', lang)
  const driverName = localized(driver, 'name', lang)
  const status   = row.session_status ?? 'PENDING'
  const cfg      = sessionStatusCfg(status)
  const isPending = String(status).toUpperCase() === 'PENDING'
  // Once the scheduled session time has passed there's nothing to accept/reject —
  // the request is stale, so we hide the actions and say so instead.
  const startMs  = row.start_time ? new Date(row.start_time).getTime() : NaN
  const isPast   = Number.isFinite(startMs) && startMs < Date.now()

  const updateStatus = useUpdateSessionStatusOnBehalf()
  const [rejectOpen, setRejectOpen] = useState(false)
  const [comment, setComment] = useState('')

  const onAccept = () => {
    updateStatus.mutate(
      { sessions_id: row.id, session_status: 1 },
      {
        onSuccess: () => toast.success(t('sessionReq.toastAccepted')),
        onError: (e: any) => toast.error(e?.response?.data?.message ?? t('sessionReq.toastAcceptFail')),
      },
    )
  }

  const submitReject = () => {
    if (!comment.trim()) { toast.error(t('sessionReq.toastReasonRequired')); return }
    updateStatus.mutate(
      { sessions_id: row.id, session_status: 2, driver_comments: comment.trim() },
      {
        onSuccess: () => { toast.success(t('sessionReq.toastRejected')); setRejectOpen(false); setComment('') },
        onError: (e: any) => toast.error(e?.response?.data?.message ?? t('sessionReq.toastRejectFail')),
      },
    )
  }

  const categoryName = translated(booking.category?.category_translations, lang)
  const cityName      = translated(booking.city?.city_translations, lang)
  const pkgName        = translated(booking.package?.package_translations, lang)
  // Unlimited packages come back either as an explicit is_unlimited flag or as
  // number_of_sessions/-1 with no flag at all — checking both so "-1" never
  // shows up literally where the ∞ symbol should be.
  const isUnlimited   = booking.package?.is_unlimited === true || booking.package?.is_unlimited === 1
    || Number(booking.package?.number_of_sessions) < 0

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden bg-brand-50/20">

      {/* ── Header ── */}
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src={user.photo_url || DUMMY_AVATAR} alt={userName} onError={onAvatarError}
            className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0 bg-gray-100" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">{userName}</div>
            <div className="text-[12px] text-gray-400">{t('sessionReq.session')} #{row.id} · {t('sessionReq.booking')} #{row.booking_id ?? booking.id ?? '—'}</div>
          </div>
        </div>
        <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-bold flex-shrink-0', cfg.cls)}>{t(cfg.key, cfg.key)}</span>
      </div>

      {/* ── Contact row ── */}
      <div className="px-4 pb-2 space-y-0.5">
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
          <span className="flex items-center gap-1 min-w-0"><Mail size={10} className="text-gray-400 flex-shrink-0" /><span className="truncate">{user.email ?? '—'}</span></span>
          <span className="flex items-center gap-1 flex-shrink-0"><Phone size={10} className="text-gray-400" />{user.phone ? `+${user.phone}` : '—'}</span>
        </div>
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Car size={10} className="text-gray-400" />{categoryName}{booking.gear_type ? ` · ${booking.gear_type}` : ''}</span>
          <span className="flex items-center gap-1"><MapPin size={10} className="text-gray-400" />{cityName}</span>
        </div>
      </div>

      {/* ── Trainer Info ── */}
      <div className="mx-4 mb-2 rounded-xl border border-gray-200 overflow-hidden bg-white/60">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50/80 border-b border-gray-100">{t('sessionReq.trainerInfo')}</div>
        <div className="px-3 py-2 flex items-center gap-2">
          {driver.photo_url && <img src={driver.photo_url} alt={driverName} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />}
          <div>
            <div className="text-sm font-bold text-gray-800">{driverName}</div>
            <div className="text-[12px] text-gray-400">{t('sessionReq.trainerId')}: #TR{String(driver.id ?? '000').padStart(3, '0')}</div>
          </div>
        </div>
      </div>

      {/* ── Session Info ── */}
      <div className="mx-4 mb-2 rounded-xl border border-gray-200 overflow-hidden bg-white/60">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50/80 border-b border-gray-100">{t('sessionReq.sessionInfo')}</div>
        <div className="px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-gray-500"><CalendarClock size={11} className="text-gray-400" />{fmtDateTime(row.start_time)} → {row.end_time ? new Date(row.end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}</span>
            <span className="font-semibold text-gray-700">{row.session_type ?? '—'}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="flex items-center gap-1"><Clock size={11} className="text-gray-400" />{t('sessionReq.sessionsRemaining')}</span>
            <span className="font-bold text-gray-800">{isUnlimited ? <InfinityIcon size={13} className="inline" /> : (row.no_of_sessions ?? '—')}</span>
          </div>
        </div>
      </div>

      {/* ── Payment Info ── */}
      <div className="mx-4 mb-4 rounded-xl border border-gray-200 overflow-hidden bg-white/60">
        <div className="px-3 py-1.5 text-xs font-bold text-gray-700 bg-gray-50/80 border-b border-gray-100">{t('sessionReq.paymentInfo')}</div>
        <div className="px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-6 h-6 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
              <Package size={11} className="text-brand-600" />
            </div>
            <div>
              <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('sessionReq.package')}</div>
              <div className="text-xs font-bold text-gray-800 truncate max-w-32">{pkgName}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-black text-brand-600">AED {Number(booking.total ?? booking.sub_total ?? 0).toLocaleString()}</div>
            {booking.payment_status && (
              <div className="flex items-center justify-end gap-1 text-[11px] text-gray-400"><CreditCard size={10} />{booking.payment_status}</div>
            )}
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      {isPending && (
        isPast ? (
          <div className="mx-4 mb-4 flex items-center justify-center gap-1.5 rounded-lg bg-gray-100 text-gray-500 text-xs font-semibold py-2.5">
            <Clock size={14} className="text-gray-400" /> {t('sessionReq.sessionTimePassed')}
          </div>
        ) : (
          <div className="mx-4 mb-4 flex items-center gap-2">
            <button
              onClick={onAccept}
              disabled={updateStatus.isPending}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-bold py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check size={14} /> {t('sessionReq.accept')}
            </button>
            <button
              onClick={() => setRejectOpen(true)}
              disabled={updateStatus.isPending}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-bold py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={14} /> {t('sessionReq.reject')}
            </button>
          </div>
        )
      )}

      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title={t('sessionReq.rejectTitle')} size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('sessionReq.rejectPrompt', { id: row.id })}
          </p>
          <Textarea
            label={t('sessionReq.reason')}
            className="min-h-24"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('sessionReq.reasonPlaceholder')}
          />
          <div className="flex items-center justify-end gap-3">
            <button onClick={() => setRejectOpen(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('sessionReq.cancel')}</button>
            <button
              onClick={submitReject}
              disabled={updateStatus.isPending || !comment.trim()}
              className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-red-500 hover:bg-red-600 disabled:opacity-50"
            >
              {updateStatus.isPending ? t('sessionReq.pleaseWait') : t('sessionReq.rejectSession')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white animate-pulse overflow-hidden">
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-1.5"><div className="h-3 bg-gray-200 rounded w-2/3" /><div className="h-2 bg-gray-100 rounded w-1/2" /></div>
        <div className="h-5 w-16 rounded-full bg-gray-200" />
      </div>
      <div className="px-4 pb-2 space-y-1"><div className="h-2 bg-gray-100 rounded w-3/4" /><div className="h-2 bg-gray-100 rounded w-1/2" /></div>
      {[1, 2, 3].map(i => <div key={i} className="mx-4 mb-2 h-14 bg-gray-100 rounded-xl" />)}
    </div>
  )
}

// ── Paginator ─────────────────────────────────────────────────────────────────
function Paginator({ page, lastPage, total, perPage, onPage, onPerPage }: {
  page: number; lastPage: number; total: number; perPage: number
  onPage: (p: number) => void; onPerPage: (n: number) => void
}) {
  const { t } = useTranslation()
  const nums = (() => {
    let s = Math.max(1, page - 2); if (s + 4 > lastPage) s = Math.max(1, lastPage - 4)
    return Array.from({ length: Math.min(5, lastPage) }, (_, i) => s + i).filter(p => p <= lastPage)
  })()
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap shadow-sm">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>{t('sessionReq.itemsPerPage')}</span>
        <select value={perPage} onChange={e => onPerPage(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400">
          {[10, 15, 20, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span>{(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} {t('sessionReq.ofItems', { n: total })}</span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1}
          className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft size={14} />
        </button>
        {nums.map(p => (
          <button key={p} onClick={() => onPage(p)}
            className={cn('w-8 h-8 rounded-lg border text-xs font-semibold',
              p === page ? 'bg-brand-500 text-white border-brand-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
            {p}
          </button>
        ))}
        <button onClick={() => onPage(Math.min(lastPage, page + 1))} disabled={page >= lastPage}
          className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40">
          <ChevronRight size={14} />
        </button>
        <span className="ml-1 text-xs text-gray-400">{t('sessionReq.of')} {lastPage}</span>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
export function BookingSessionRequestsPage() {
  const { t } = useTranslation()
  const [page,    setPage]    = useState(1)
  const [perPage, setPerPage] = useState(15)

  const { data, isLoading } = useQuery({
    queryKey: ['booking-session-requests', page, perPage],
    queryFn:  () => bookingService.sessionRequests({ page, limit: perPage }),
    staleTime: 30_000,
  })

  const rows: any[] = (data as any)?.data ?? (data as any) ?? []
  const total       = (data as any)?.meta?.total ?? (data as any)?.total ?? rows.length
  const lastPage    = Math.ceil(total / perPage) || 1

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
          <span>{t('dash.dashboard')}</span><span>/</span>
          <span className="text-brand-500 font-semibold">{t('sessionReq.title')}</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900">{t('sessionReq.title')}</h1>
      </div>

      {/* ── Stat tile ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatTile
          label={t('sessionReq.totalRequests')} value={total}
          icon={<Calendar size={22} className="text-brand-500" />}
          bg="bg-brand-50" iconBg="bg-brand-100" textColor="text-brand-700"
        />
      </div>

      {/* ── Card grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : rows.length === 0
            ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Calendar size={24} className="text-gray-300" />
                </div>
                <p className="text-sm font-semibold text-gray-500">{t('sessionReq.noRequests')}</p>
              </div>
            )
            : rows.map((row: any) => <SessionRequestCard key={row.id} row={row} />)
        }
      </div>

      {/* ── Pagination ── */}
      {!isLoading && total > 0 && (
        <Paginator page={page} lastPage={lastPage} total={total} perPage={perPage}
          onPage={setPage} onPerPage={n => { setPerPage(n); setPage(1) }} />
      )}
    </div>
  )
}
